import React, { useEffect, useState } from "react";
import { CreditCard, Gift, Zap } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast";

// Offre MyALFA Link du dossier (onglet « MyALFA Link » de la fiche client) —
// décision du 30/10/2026 : chaque STRUCTURE a sa propre offre. Tout le monde
// la voit ; Jonathan et Anthony peuvent en offrir une hors paiement
// (`peut_modifier`, contrôlé aussi côté backend, offreLinkController.js).
// Impossible tant que le client paie par carte : MyALFA Link le refuse.

const API = "https://backend-myalfa.vercel.app/api/clients";
const messageErreur = (err, defaut) => err?.response?.data?.message || defaut;
const dateFr = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : null);

const OFFRES = {
  gratuit: { label: "Gratuit", classe: "bg-slate-100 text-slate-600 border-slate-200" },
  essentiel: { label: "Essentiel", classe: "bg-sky-50 text-sky-700 border-sky-200" },
  pro: { label: "Pro", classe: "bg-[#9B1B5D]/10 text-[#9B1B5D] border-[#9B1B5D]/20" },
  business: { label: "Business", classe: "bg-amber-50 text-amber-700 border-amber-200" },
};

function detail(etat) {
  if (etat.paiement_en_ligne) {
    const fin = dateFr(etat.fin_periode);
    if (etat.statut === "impaye") return "Payée par carte — dernier prélèvement échoué";
    return etat.annulation_fin_periode
      ? `Payée par carte — résiliée, active jusqu'au ${fin}`
      : `Payée par carte${fin ? ` — prochain prélèvement le ${fin}` : ""}`;
  }
  if (etat.source === "offert" && etat.offre !== "gratuit") return "Offerte par le cabinet";
  if (etat.offre === "gratuit") return "Offre par défaut, aucune facturation";
  return "Posée à la main";
}

export default function OffreMyalfaLink({ client }) {
  const [etat, setEtat] = useState(null); // null = chargement
  const [erreur, setErreur] = useState("");
  const [choix, setChoix] = useState("gratuit");
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!client?.id) return;
    let annule = false;
    setEtat(null);
    setErreur("");
    httpClient
      .get(`${API}/${client.id}/offre-link`)
      .then(({ data }) => {
        if (annule) return;
        setEtat(data);
        setChoix(data.offre);
      })
      .catch((err) => !annule && setErreur(messageErreur(err, "Offre MyALFA Link indisponible.")));
    return () => {
      annule = true;
    };
  }, [client?.id]);

  const appliquer = async () => {
    const label = OFFRES[choix].label;
    const ok = await dialog.confirm(
      choix === "gratuit"
        ? `Repasser ${client.nom} en offre Gratuit ? Les fonctionnalités des offres payantes ne lui seront plus accessibles.`
        : `Offrir l'offre ${label} à ${client.nom} ? Le client n'aura rien à payer et ne pourra pas la modifier lui-même.`,
      { title: choix === "gratuit" ? "Repasser en Gratuit" : `Offrir l'offre ${label}`, confirmLabel: "Confirmer" },
    );
    if (!ok) return;
    setEnvoi(true);
    try {
      const { data } = await httpClient.put(`${API}/${client.id}/offre-link`, { plan: choix });
      setEtat(data);
      setChoix(data.offre);
      showStatusToast({
        status: "success",
        titre: choix === "gratuit" ? "Offre Gratuit appliquée" : `Offre ${label} offerte`,
        message: client.nom,
      });
    } catch (err) {
      showStatusToast({ status: "error", titre: "Offre non modifiée", message: messageErreur(err, "L'offre n'a pas pu être modifiée.") });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200">
      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#9B1B5D]" />
        Offre MyAlfa
        {etat && (
          <span className={`ml-auto text-[11px] font-semibold rounded-full px-2 py-0.5 border ${(OFFRES[etat.offre] || OFFRES.gratuit).classe}`}>
            {(OFFRES[etat.offre] || OFFRES.gratuit).label}
          </span>
        )}
      </h3>

      {erreur ? (
        <p className="text-xs text-red-600">{erreur}</p>
      ) : !etat ? (
        <div className="space-y-2 animate-pulse" aria-hidden="true">
          <div className="h-3 w-48 rounded bg-slate-200" />
          <div className="h-3 w-64 rounded bg-slate-200" />
        </div>
      ) : (
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-xs text-slate-600">
            {etat.paiement_en_ligne ? (
              <CreditCard className="w-3.5 h-3.5 text-slate-400" />
            ) : etat.source === "offert" && etat.offre !== "gratuit" ? (
              <Gift className="w-3.5 h-3.5 text-slate-400" />
            ) : null}
            {detail(etat)}
          </p>
          <p className="text-[11px] text-slate-500">
            Cette offre ne vaut que pour ce dossier.{" "}
            {etat.gestionnaires.length
              ? `Gérée côté client par ${etat.gestionnaires.map((g) => g.nom).join(", ")}.`
              : "Aucun contact de l'Annuaire n'est rattaché à ce dossier."}
          </p>

          {etat.peut_modifier && (
            <div className="pt-3 border-t border-slate-100 space-y-2">
              <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Gift className="w-3.5 h-3.5" /> Offrir une offre (hors paiement)
              </p>
              {etat.paiement_en_ligne ? (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                  Le client paie son offre par carte : l'abonnement doit d'abord être résilié (par le client depuis « Gérer mon abonnement », ou depuis Stripe).
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <select
                    value={choix}
                    onChange={(e) => setChoix(e.target.value)}
                    disabled={envoi}
                    className="flex-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#9B1B5D]"
                  >
                    {Object.entries(OFFRES).map(([id, o]) => (
                      <option key={id} value={id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={appliquer}
                    disabled={envoi || choix === etat.offre}
                    className="rounded-lg bg-[#840040] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#6a0033] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {envoi ? "Enregistrement…" : "Appliquer"}
                  </button>
                </div>
              )}
              <p className="text-[10px] text-slate-400">Réservé à Jonathan et Anthony.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
